import axios from "axios";
import { useState } from "react";
import {
  FaIndustry, FaCogs, FaRobot, FaUser, FaMapMarkerAlt, FaWhatsapp,
  FaHome, FaBuilding, FaSearch, FaLock, FaSun, FaChartLine,
  FaArrowRight, FaBolt, FaRulerCombined, FaLeaf,
} from "react-icons/fa";

const RED = "#B7191A";
const INK = "#1A1A1A";
const SUBINK = "#4B4B4B";
const BORDER = "#E4E1DC";
const CREAM = "#FAF8F5";
const NAVY = "#0E1B33";

/* =========================================================
   STYLES
   ========================================================= */
const styles = {
  page: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    color: INK,
    backgroundColor: "#FFFFFF",
    width: "100%",
    overflowX: "hidden",
    marginBottom: "10px",
  },

  /* ---------- Hero (full width image, original) ---------- */
  hero: {
    width: "100%",
    lineHeight: 0,
  },
  heroImage: {
    width: "100%",
    height: "auto",
    display: "block",
  },

  /* ---------- Form section (now below calculator) ---------- */
  formSection: {
    maxWidth: "980px",
    margin: "80px auto 0",
    padding: "0 24px",
    position: "relative",
    zIndex: 2,
  },
  formCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 24px 60px rgba(14, 27, 51, 0.10)",
    border: `1px solid ${BORDER}`,
    padding: "40px 36px",
  },
  formHeading: {
    textAlign: "center",
    fontSize: "26px",
    fontWeight: 800,
    margin: 0,
    letterSpacing: "-0.3px",
  },
  formHeadingAccent: {
    color: RED,
  },
  formSubheading: {
    textAlign: "center",
    marginTop: "8px",
    color: SUBINK,
    fontSize: "13.5px",
  },
  formGrid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "18px",
    marginTop: "30px",
  },
  fieldLabel: {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "6px",
    color: INK,
  },
  inputWrap: {
    display: "flex",
    alignItems: "center",
    border: `1px solid ${BORDER}`,
    borderRadius: "10px",
    padding: "0 14px",
    background: "#fff",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  inputIcon: {
    color: "#9A9A9A",
    fontSize: "14px",
    marginRight: "8px",
    display: "flex",
    alignItems: "center",
  },
  input: {
    border: "none",
    outline: "none",
    padding: "13px 0",
    width: "100%",
    fontSize: "14px",
    background: "transparent",
    color: INK,
    fontFamily: "inherit",
  },
  prefix: {
    fontSize: "14px",
    color: SUBINK,
    marginRight: "6px",
  },
  sectionLabel: {
    fontSize: "13px",
    fontWeight: 600,
    marginTop: "30px",
    marginBottom: "10px",
    display: "block",
  },
  optionGrid4: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "14px",
  },
  optionCard: (active) => ({
    border: `1.5px solid ${active ? RED : BORDER}`,
    borderRadius: "12px",
    padding: "20px 8px",
    textAlign: "center",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    color: active ? RED : INK,
    background: active ? "#FDF2F1" : "#fff",
    boxShadow: active ? "0 0 0 3px rgba(183,25,26,0.06)" : "none",
    transition: "all 0.15s ease",
  }),
  optionIcon: {
    fontSize: "22px",
  },
  optionLabel: {
    fontSize: "13px",
    fontWeight: 600,
  },
  radioGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
  },
  radioRow: (active) => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    border: `1px solid ${active ? RED : BORDER}`,
    borderRadius: "10px",
    padding: "13px 16px",
    cursor: "pointer",
    fontSize: "13.5px",
    color: INK,
    background: active ? "#FDF2F1" : "#fff",
    boxShadow: active ? "0 0 0 3px rgba(183,25,26,0.06)" : "none",
    transition: "all 0.15s ease",
  }),
  radioDot: (active) => ({
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    border: `1.5px solid ${active ? RED : "#B9B9B9"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "border-color 0.15s ease",
  }),
  radioDotInner: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: RED,
  },
  submitBtn: (disabled) => ({
    width: "100%",
    marginTop: "24px",
    background: disabled ? "#D79392" : RED,
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "17px 0",
    fontSize: "15px",
    fontWeight: 700,
    letterSpacing: "0.3px",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    transition: "background 0.2s ease",
  }),
  submitNote: {
    textAlign: "center",
    marginTop: "16px",
    fontSize: "12px",
    color: "#8A8A8A",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  statusBanner: (kind) => ({
    marginTop: "18px",
    borderRadius: "10px",
    padding: "13px 16px",
    fontSize: "13px",
    fontWeight: 500,
    textAlign: "center",
    background: kind === "error" ? "#FDECEC" : "#EAF7EE",
    color: kind === "error" ? RED : "#1E7D3B",
    border: `1px solid ${kind === "error" ? "#F3C6C6" : "#BFE6CB"}`,
  }),

  /* =========================================================
     CALCULATOR — stepper rail + ledger
     ========================================================= */
  calcSection: {
    maxWidth: "1120px",
    margin: "80px auto 0",
    padding: "0 24px",
  },
  calcHeaderRow: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "20px",
    marginBottom: "32px",
    borderBottom: `1px solid ${BORDER}`,
    paddingBottom: "24px",
  },
  calcHeaderLeft: { maxWidth: "560px" },
  calcKicker: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    color: RED,
    margin: 0,
  },
  calcHeading: {
    fontSize: "34px",
    fontWeight: 800,
    margin: "8px 0 0",
    lineHeight: 1.15,
    letterSpacing: "-0.8px",
  },
  calcMeta: {
    fontSize: "13px",
    color: SUBINK,
    marginTop: "8px",
  },
  calcShell: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1.1fr)",
    borderRadius: "18px",
    overflow: "hidden",
    border: `1px solid ${BORDER}`,
    background: "#fff",
    boxShadow: "0 30px 60px rgba(14,27,51,0.08)",
  },
  calcRail: {
    background: NAVY,
    padding: "40px 36px",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
  },
  calcStep: {
    display: "flex",
    gap: "14px",
    marginBottom: "28px",
  },
  calcStepNum: (active) => ({
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 700,
    border: `1.5px solid ${active ? "#fff" : "rgba(255,255,255,0.25)"}`,
    color: active ? NAVY : "rgba(255,255,255,0.55)",
    background: active ? "#fff" : "transparent",
  }),
  calcStepBody: { flex: 1, minWidth: 0 },
  calcStepLabel: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.5)",
    marginBottom: "8px",
  },
  calcInput: {
    width: "100%",
    background: "transparent",
    border: "none",
    borderBottom: "1px solid rgba(255,255,255,0.2)",
    outline: "none",
    padding: "6px 0 10px",
    fontSize: "17px",
    fontWeight: 500,
    color: "#fff",
    fontFamily: "inherit",
  },
  calcSelect: {
    width: "100%",
    background: "transparent",
    border: "none",
    borderBottom: "1px solid rgba(255,255,255,0.2)",
    outline: "none",
    padding: "6px 0 10px",
    fontSize: "17px",
    fontWeight: 500,
    color: "#fff",
    fontFamily: "inherit",
  },
  calcSelectOption: { color: "#000" },
  calcBigStat: {
    marginTop: "auto",
    paddingTop: "28px",
    borderTop: "1px solid rgba(255,255,255,0.15)",
  },
  calcBigStatLabel: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.5)",
    marginBottom: "8px",
  },
  calcBigStatNum: {
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: "52px",
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: "-1.5px",
    color: "#fff",
  },
  calcBigStatCaption: {
    fontSize: "12px",
    color: "rgba(255,255,255,0.5)",
    marginTop: "8px",
  },
  calcOutput: {
    padding: "40px 40px",
    display: "flex",
    flexDirection: "column",
  },
  calcOutputEyebrow: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    color: SUBINK,
    marginBottom: "20px",
  },
  calcLedger: {
    display: "flex",
    flexDirection: "column",
    borderTop: `1px solid ${BORDER}`,
  },
  calcLedgerRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    padding: "15px 0",
    borderBottom: `1px solid ${BORDER}`,
    gap: "16px",
  },
  calcLedgerLabel: {
    fontSize: "13.5px",
    color: SUBINK,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  calcLedgerValue: {
    fontSize: "16px",
    fontWeight: 700,
    color: INK,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  calcTotalRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    padding: "22px 0 0",
    marginTop: "6px",
    borderTop: `2px solid ${INK}`,
    gap: "16px",
  },
  calcTotalLabel: {
    fontSize: "14px",
    fontWeight: 700,
    color: INK,
  },
  calcTotalValue: {
    fontFamily: "Georgia, serif",
    fontSize: "30px",
    fontWeight: 700,
    color: RED,
    letterSpacing: "-0.5px",
  },
  calcActionRow: { marginTop: "28px" },
  calcBtn: (disabled) => ({
    width: "100%",
    background: disabled ? "#999" : RED,
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    padding: "16px 0",
    fontSize: "14px",
    fontWeight: 700,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "background 0.2s",
  }),
  calcErrorBox: {
    marginTop: "20px",
    padding: "12px 16px",
    borderRadius: "10px",
    background: "#fff1f1",
    border: "1px solid #f2c2c2",
    color: RED,
    fontSize: "13px",
  },

  /* results-reveal area */
  resultsWrap: {
    marginTop: "32px",
    padding: "36px 40px",
    borderRadius: "18px",
    background: CREAM,
    border: `1px solid ${BORDER}`,
  },
  resultsTop: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)",
    gap: "40px",
    alignItems: "start",
  },
  resultsBig: {
    borderRight: `1px solid ${BORDER}`,
    paddingRight: "40px",
  },
  resultsBigLabel: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    color: SUBINK,
    marginBottom: "10px",
  },
  resultsBigValue: {
    fontFamily: "Georgia, serif",
    fontSize: "64px",
    fontWeight: 700,
    lineHeight: 1,
    color: RED,
    letterSpacing: "-2px",
  },
  resultsBigUnit: {
    fontSize: "20px",
    fontWeight: 600,
    color: INK,
    marginLeft: "6px",
    fontFamily: "'Inter', sans-serif",
  },
  resultsBigSub: {
    fontSize: "13px",
    color: SUBINK,
    marginTop: "12px",
    lineHeight: 1.5,
  },
  resultsStats: {
    display: "flex",
    flexDirection: "column",
  },
  resultsStatRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 0",
    borderBottom: `1px solid ${BORDER}`,
    gap: "16px",
  },
  resultsStatLabel: {
    fontSize: "13px",
    color: SUBINK,
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  resultsStatIcon: {
    color: RED,
    fontSize: "13px",
    display: "flex",
  },
  resultsStatValue: {
    fontSize: "16px",
    fontWeight: 700,
    color: INK,
    fontVariantNumeric: "tabular-nums",
  },
  monthlyStrip: {
    marginTop: "32px",
    paddingTop: "28px",
    borderTop: `1px solid ${BORDER}`,
  },
  monthlyStripTitle: {
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: SUBINK,
    marginBottom: "16px",
  },
  monthlyBars: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: "8px",
    alignItems: "end",
    height: "120px",
  },
  monthlyBarCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
    height: "100%",
    gap: "6px",
  },
  monthlyBar: (pct) => ({
    width: "100%",
    height: `${pct}%`,
    background: RED,
    borderRadius: "4px 4px 0 0",
    minHeight: "6px",
    transition: "height 0.3s ease",
  }),
  monthlyBarLabel: {
    fontSize: "9.5px",
    color: SUBINK,
    fontWeight: 600,
    textTransform: "uppercase",
  },

  /* =========================================================
     WHY CHOOSE — 2×2 magazine layout
     ========================================================= */
  whySection: {
    background: "#fff",
    marginTop: "96px",
    padding: "80px 24px",
    borderTop: `1px solid ${BORDER}`,
  },
  whyInner: { maxWidth: "1120px", margin: "0 auto" },
  whyHeader: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "20px",
    marginBottom: "48px",
    paddingBottom: "24px",
    borderBottom: `1px solid ${BORDER}`,
  },
  whyHeading: {
    fontSize: "34px",
    fontWeight: 800,
    margin: 0,
    letterSpacing: "-0.8px",
    lineHeight: 1.15,
    maxWidth: "520px",
  },
  whyHeadingAccent: {
    color: RED,
    fontStyle: "italic",
    fontFamily: "Georgia, serif",
  },
  whySub: {
    fontSize: "13.5px",
    color: SUBINK,
    maxWidth: "320px",
    lineHeight: 1.6,
  },
  whyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
  },
  whyCard: (accent) => ({
    display: "flex",
    gap: "20px",
    padding: "28px",
    borderRadius: "16px",
    border: `1px solid ${BORDER}`,
    background: accent ? CREAM : "#fff",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  }),
  whyIconBox: {
    width: "52px",
    height: "52px",
    borderRadius: "12px",
    background: RED,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    flexShrink: 0,
  },
  whyCardBody: { flex: 1 },
  whyCardTitle: {
    fontSize: "16px",
    fontWeight: 700,
    margin: "0 0 8px",
    letterSpacing: "-0.2px",
  },
  whyCardText: {
    fontSize: "13.5px",
    color: SUBINK,
    lineHeight: 1.6,
    margin: 0,
  },

  /* banner */
  banner: {
    background: NAVY,
    color: "#fff",
    position: "relative",
    overflow: "hidden",
    marginTop: "0",
  },
  bannerImage: { width: "100%", height: "auto", display: "block" },
};

/* =========================================================
   DATA
   ========================================================= */
const lookingForOptions = [
  { key: "home", label: "Home", icon: FaHome },
  { key: "office", label: "Office", icon: FaBuilding },
  { key: "factory", label: "Factory", icon: FaIndustry },
  { key: "exploring", label: "Just Exploring", icon: FaSearch },
];

const billOptions = [
  "₹0 – ₹2,000",
  "₹2,000 – ₹5,000",
  "₹5,000 – ₹10,000",
  "₹10,000 – ₹25,000",
  "₹25,000 – ₹50,000",
  "₹50,000+",
];

/* =========================================================
   SOLAR SAVINGS CALCULATOR — LOGIC UNCHANGED
   ========================================================= */
const SolarSavingsCalculator = () => {
  const [pin, setPin] = useState("");
  const [customerType, setCustomerType] = useState("Residential");
  const [monthlyBill, setMonthlyBill] = useState("");
  const [unitCost, setUnitCost] = useState("7");
  const [loading, setLoading] = useState(false);
  const [calculated, setCalculated] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const formatNumber = (value, decimals = 0) => {
    if (value === undefined || value === null || Number.isNaN(Number(value))) return "0";
    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null || Number.isNaN(Number(value))) return "₹0";
    return Number(value).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    });
  };

  // ⬇⬇⬇ IDENTICAL LOGIC ⬇⬇⬇
  const calculateSavings = async () => {
    setError("");
    setCalculated(false);
    setResult(null);

    if (!/^\d{6}$/.test(pin)) {
      setError("Please enter a valid 6-digit PIN code.");
      return;
    }
    const bill = Number(monthlyBill);
    const rate = Number(unitCost);

    if (!bill || bill <= 0) {
      setError("Please enter a valid monthly electricity bill.");
      return;
    }
    if (!rate || rate <= 0) {
      setError("Please enter a valid electricity rate.");
      return;
    }

    try {
      setLoading(true);

      const pinResponse = await fetch(
        `https://api.pincodeapi.in/api/v1/pincode/${pin}`
      );
      if (!pinResponse.ok) throw new Error("Unable to find this PIN code.");
      const pinData = await pinResponse.json();
      if (
        !pinData.success ||
        !pinData.data ||
        !Array.isArray(pinData.data.post_offices) ||
        pinData.data.post_offices.length === 0
      ) {
        throw new Error("PIN code not found. Please enter a valid Indian PIN code.");
      }
      const postOffice = pinData.data.post_offices.find(
        (o) =>
          o.latitude !== null &&
          o.latitude !== undefined &&
          o.longitude !== null &&
          o.longitude !== undefined
      );
      if (!postOffice) {
        throw new Error("Latitude and longitude are not available for this PIN code.");
      }
      const latitude = Number(postOffice.latitude);
      const longitude = Number(postOffice.longitude);
      const state = postOffice.state || "";
      const district = postOffice.district || "";
      const officeName = postOffice.office_name || "";
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        throw new Error("Invalid location coordinates received for this PIN code.");
      }

      const monthlyUnits = bill / rate;
      const annualUnits = monthlyUnits * 12;

      const solarUrl = new URL("https://archive-api.open-meteo.com/v1/archive");
      solarUrl.searchParams.set("latitude", latitude);
      solarUrl.searchParams.set("longitude", longitude);
      solarUrl.searchParams.set("start_date", "2025-01-01");
      solarUrl.searchParams.set("end_date", "2025-12-31");
      solarUrl.searchParams.set("hourly", "global_tilted_irradiance");
      solarUrl.searchParams.set("tilt", "25");
      solarUrl.searchParams.set("azimuth", "0");
      solarUrl.searchParams.set("timezone", "auto");

      const solarResponse = await fetch(solarUrl.toString());
      if (!solarResponse.ok) throw new Error("Unable to retrieve solar radiation data.");
      const solarData = await solarResponse.json();
      if (
        !solarData.hourly ||
        !Array.isArray(solarData.hourly.time) ||
        !Array.isArray(solarData.hourly.global_tilted_irradiance)
      ) {
        throw new Error("Solar radiation data was not returned for this location.");
      }
      const times = solarData.hourly.time;
      const gtiValues = solarData.hourly.global_tilted_irradiance;
      if (times.length === 0 || gtiValues.length === 0) {
        throw new Error("No solar radiation data was available.");
      }

      const monthlyGTI = {};
      for (let i = 0; i < times.length; i++) {
        const date = times[i];
        const month = Number(date.substring(5, 7));
        const gti = Number(gtiValues[i]);
        if (!Number.isFinite(gti) || gti < 0) continue;
        if (!monthlyGTI[month]) monthlyGTI[month] = 0;
        monthlyGTI[month] += gti / 1000;
      }

      const PERFORMANCE_RATIO = 0.8;
      const MONTH_NAMES = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];

      const monthlyGenerationPerKw = MONTH_NAMES.map((monthName, index) => {
        const monthNumber = index + 1;
        const gti = monthlyGTI[monthNumber] || 0;
        const generation = gti * PERFORMANCE_RATIO;
        return { month: monthName, monthNumber, gti, generation };
      });

      const annualGenerationPerKw = monthlyGenerationPerKw.reduce(
        (t, m) => t + m.generation, 0
      );
      if (!annualGenerationPerKw || annualGenerationPerKw <= 0) {
        throw new Error("Unable to calculate solar generation for this location.");
      }

      const exactSystemSize = annualUnits / annualGenerationPerKw;
      const systemSize = Math.ceil(exactSystemSize * 10) / 10;

      const monthlyResults = monthlyGenerationPerKw.map((month) => {
        const generation = month.generation * systemSize;
        const savings = generation * rate;
        return {
          month: month.month,
          monthNumber: month.monthNumber,
          gti: month.gti,
          generation,
          savings,
        };
      });

      const annualGeneration = monthlyResults.reduce((t, m) => t + m.generation, 0);
      const annualSavings = monthlyResults.reduce((t, m) => t + m.savings, 0);
      const averageMonthlySavings = annualSavings / 12;

      setResult({
        pin, state, district, officeName, latitude, longitude, customerType,
        monthlyBill: bill, electricityRate: rate, monthlyUnits, annualUnits,
        annualGenerationPerKw, exactSystemSize, systemSize,
        annualGeneration, annualSavings, averageMonthlySavings,
        monthly: monthlyResults,
      });
      setCalculated(true);
    } catch (error) {
      console.error("Solar calculation error:", error);
      setError(
        error.message ||
        "Something went wrong while calculating your solar savings."
      );
    } finally {
      setLoading(false);
    }
  };
  // ⬆⬆⬆ END — IDENTICAL LOGIC ⬆⬆⬆

 

  return (
    <div style={styles.calcSection}>
      <div style={styles.calcHeaderRow}>
        <div style={styles.calcHeaderLeft}>
          <p style={styles.calcKicker}>Solar Estimate</p>
          <h2 style={styles.calcHeading}>
            How much can you save <br />with solar?
          </h2>
        </div>
      </div>

      <div className="gs-calc-shell" style={styles.calcShell}>
        {/* LEFT RAIL */}
        <div style={styles.calcRail}>
          <div style={styles.calcStep}>
            <div style={styles.calcStepNum(true)}>1</div>
            <div style={styles.calcStepBody}>
              <div style={styles.calcStepLabel}>Your PIN Code</div>
              <input
                type="text"
                value={pin}
                maxLength={6}
                placeholder="6-digit PIN"
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                style={styles.calcInput}
              />
            </div>
          </div>

          <div style={styles.calcStep}>
            <div style={styles.calcStepNum(!!monthlyBill)}>2</div>
            <div style={styles.calcStepBody}>
              <div style={styles.calcStepLabel}>Monthly Bill (₹)</div>
              <input
                type="number"
                min="0"
                value={monthlyBill}
                placeholder="e.g. 5000"
                onChange={(e) => setMonthlyBill(e.target.value)}
                style={styles.calcInput}
              />
            </div>
          </div>

          <div style={styles.calcStep}>
            <div style={styles.calcStepNum(!!unitCost)}>3</div>
            <div style={styles.calcStepBody}>
              <div style={styles.calcStepLabel}>Rate (₹ / unit)</div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={unitCost}
                placeholder="e.g. 7"
                onChange={(e) => setUnitCost(e.target.value)}
                style={styles.calcInput}
              />
            </div>
          </div>

          <div style={styles.calcStep}>
            <div style={styles.calcStepNum(true)}>4</div>
            <div style={styles.calcStepBody}>
              <div style={styles.calcStepLabel}>Customer Type</div>
              <select
                value={customerType}
                onChange={(e) => setCustomerType(e.target.value)}
                style={styles.calcSelect}
              >
                <option style={styles.calcSelectOption} value="Residential">Residential</option>
                <option style={styles.calcSelectOption} value="Commercial">Commercial</option>
                <option style={styles.calcSelectOption} value="Industrial">Industrial</option>
              </select>
            </div>
          </div>

          <div style={styles.calcBigStat}>
            <div style={styles.calcBigStatLabel}>Your Monthly Bill</div>
            <div className="gs-calc-big-number" style={styles.calcBigStatNum}>
              {monthlyBill ? `₹${formatNumber(monthlyBill)}` : "₹—"}
            </div>
            <div style={styles.calcBigStatCaption}>
              Enter your bill to see live estimate →
            </div>
          </div>
        </div>

        {/* RIGHT OUTPUT */}
        <div style={styles.calcOutput}>
          <div style={styles.calcOutputEyebrow}>
            {calculated && result
              ? `Estimate for ${result.district}${result.state ? ", " + result.state : ""}`
              : "Your Solar Snapshot"}
          </div>

          {!calculated && !loading && (
            <>
              <div style={styles.calcLedger}>
                <LedgerRow label="Recommended System Size" value="—" />
                <LedgerRow label="Estimated Monthly Units" value="—" />
                <LedgerRow label="Expected Annual Generation" value="—" />
                <LedgerRow label="Average Monthly Savings" value="—" />
                <LedgerRow label="Estimated Annual Savings" value="—" />
              </div>
              <div style={styles.calcActionRow}>
                <button
                  type="button"
                  onClick={calculateSavings}
                  disabled={loading}
                  style={styles.calcBtn(loading)}
                >
                  Calculate My Savings
                </button>
              </div>
              {error && <div style={styles.calcErrorBox}>{error}</div>}
            </>
          )}

          {loading && (
            <>
              <div style={styles.calcLedger}>
                <LedgerRow label="Recommended System Size" value="…" />
                <LedgerRow label="Estimated Monthly Units" value="…" />
                <LedgerRow label="Expected Annual Generation" value="…" />
                <LedgerRow label="Average Monthly Savings" value="…" />
                <LedgerRow label="Estimated Annual Savings" value="…" />
              </div>
              <div style={styles.calcActionRow}>
                <button type="button" disabled style={styles.calcBtn(true)}>
                  Calculating...
                </button>
              </div>
            </>
          )}

          {calculated && result && (
            <>
              <div style={styles.calcLedger}>
                <LedgerRow
                  label="Recommended System Size"
                  value={`${formatNumber(result.systemSize, 1)} kW`}
                />
                <LedgerRow
                  label="Estimated Monthly Units"
                  value={`${formatNumber(result.monthlyUnits)} units`}
                />
                <LedgerRow
                  label="Expected Annual Generation"
                  value={`${formatNumber(result.annualGeneration)} kWh`}
                />
                <LedgerRow
                  label="Average Monthly Savings"
                  value={formatCurrency(result.averageMonthlySavings)}
                />
              </div>
              <div style={styles.calcTotalRow}>
                <div style={styles.calcTotalLabel}>Estimated Annual Savings</div>
                <div style={styles.calcTotalValue}>
                  {formatCurrency(result.annualSavings)}
                </div>
              </div>
              <div style={styles.calcActionRow}>
                <button
                  type="button"
                  onClick={calculateSavings}
                  disabled={loading}
                  style={styles.calcBtn(loading)}
                >
                  Recalculate
                </button>
              </div>
              {error && <div style={styles.calcErrorBox}>{error}</div>}
            </>
          )}
        </div>
      </div>

      {/* RESULTS BLOCK */}
      {calculated && result && (
        <div style={styles.resultsWrap}>
          <div className="gs-results-top" style={styles.resultsTop}>
            <div style={styles.resultsBig}>
              <div style={styles.resultsBigLabel}>Recommended System</div>
              <div style={styles.resultsBigValue}>
                {formatNumber(result.systemSize, 1)}
                <span style={styles.resultsBigUnit}>kW</span>
              </div>
              <div style={styles.resultsBigSub}>
                Sized for {formatNumber(result.monthlyUnits)} units / month at ₹
                {result.electricityRate}/unit in {result.district}
                {result.state ? `, ${result.state}` : ""}.
              </div>
            </div>

            <div style={styles.resultsStats}>
              <StatRow
                icon={<FaBolt />}
                label="Monthly Units Consumed"
                value={formatNumber(result.monthlyUnits)}
              />
              <StatRow
                icon={<FaSun />}
                label="Annual Generation"
                value={`${formatNumber(result.annualGeneration)} kWh`}
              />
              <StatRow
                icon={<FaChartLine />}
                label="Avg. Monthly Savings"
                value={formatCurrency(result.averageMonthlySavings)}
              />
              <StatRow
                icon={<FaLeaf />}
                label="Annual Savings"
                value={formatCurrency(result.annualSavings)}
              />
              <StatRow
                icon={<FaRulerCombined />}
                label="Customer Type"
                value={result.customerType}
              />
              <StatRow
                icon={<FaMapMarkerAlt />}
                label="Location"
                value={`${result.district}${result.state ? ", " + result.state : ""}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* Helper components */
const LedgerRow = ({ label, value }) => (
  <div style={styles.calcLedgerRow}>
    <div style={styles.calcLedgerLabel}>{label}</div>
    <div style={styles.calcLedgerValue}>{value}</div>
  </div>
);

const StatRow = ({ icon, label, value }) => (
  <div style={styles.resultsStatRow}>
    <div style={styles.resultsStatLabel}>
      <span style={styles.resultsStatIcon}>{icon}</span>
      {label}
    </div>
    <div style={styles.resultsStatValue}>{value}</div>
  </div>
);

const WhyCard = ({ icon, title, text, accent }) => (
  <div style={styles.whyCard(accent)}>
    <div style={styles.whyIconBox}>{icon}</div>
    <div style={styles.whyCardBody}>
      <h3 style={styles.whyCardTitle}>{title}</h3>
      <p style={styles.whyCardText}>{text}</p>
    </div>
  </div>
);

/* =========================================================
   MAIN EXPORT — Calculator first, then Form
   ========================================================= */
export default function SolarSaving() {
  const [lookingFor, setLookingFor] = useState("home");
  const [bill, setBill] = useState("₹0 – ₹2,000");
  const [form, setForm] = useState({ name: "", pin: "", whatsapp: "", salesRepresentative: "" });
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your full name.";
    if (!/^\d{6}$/.test(form.pin.trim()))
      return "Please enter a valid 6-digit PIN code.";
    if (!/^\d{10}$/.test(form.whatsapp.trim()))
      return "Please enter a valid 10-digit WhatsApp number.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setStatus("error");
      setErrorMessage(validationError);
      return;
    }
    setStatus("submitting");
    setErrorMessage("");

    let utm = localStorage.getItem("utm_data") || null;
    let params = new URLSearchParams(window.location.search);
    let qrCode = params.get("qr");

    const payload = {
      name: form.name.trim(),
      pin: form.pin.trim(),
      whatsapp: form.whatsapp.trim(),
      salesRepresentative: form.salesRepresentative || null,
      lookingFor,
      monthlyBill: bill,
      utm,
      qrCode: qrCode || null,
    };


    try { 
      // await axios.post('http://localhost:1008/solar-saving-contact',payload);
      await axios.post("https://gautamsolar.us/solar-saving-contact", payload);
      setStatus("success");
      setForm({ name: "", pin: "", whatsapp: "", salesRepresentative: "" });
      setLookingFor("home");
      setBill("₹0 – ₹2,000");
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        "Something went wrong while submitting your details. Please try again."
      );
    }
  };

  const isSubmitting = status === "submitting";

  return (
    <div style={styles.page}>
      <style>{`
        * { box-sizing: border-box; }
        .gs-input::placeholder { color: #9A9A9A; }

        .gs-input-wrap:focus-within {
          border-color: ${RED} !important;
          box-shadow: 0 0 0 3px rgba(183, 25, 26, 0.08) !important;
        }
        .gs-calc-input:focus,
        .gs-calc-select:focus {
          border-bottom-color: #fff !important;
        }

        @media (max-width: 900px) {
          .gs-form-grid3 { grid-template-columns: 1fr !important; }
          .gs-option-grid4 { grid-template-columns: repeat(2, 1fr) !important; }
          .gs-radio-grid { grid-template-columns: 1fr !important; }
          .gs-calc-shell { grid-template-columns: 1fr !important; }
          .gs-results-top { grid-template-columns: 1fr !important; }
          .gs-results-big { border-right: none !important; padding-right: 0 !important; border-bottom: 1px solid ${BORDER}; padding-bottom: 28px; }
          .gs-why-grid { grid-template-columns: 1fr !important; }
          .gs-monthly-bars { grid-template-columns: repeat(6, 1fr) !important; height: auto; }
          .gs-monthly-bar-col { height: 90px; }
        }
        @media (max-width: 600px) {
          .gs-form-card { padding: 24px 18px !important; }
          .gs-calc-heading { font-size: 26px !important; }
          .gs-results-big-value { font-size: 48px !important; }
          .gs-monthly-bars { grid-template-columns: repeat(4, 1fr) !important; }
        }
      `}</style>

      {/* ============================================================
          HERO (full width image)
          ============================================================ */}
      <div style={styles.hero}>
        <img
          src="/site_visit.webp"
          alt="Thank you for visiting Gautam Solar"
          style={styles.heroImage}
        />
      </div>

      {/* ============================================================
          CALCULATOR FIRST
          ============================================================ */}
      <SolarSavingsCalculator />

      {/* ============================================================
          FORM BELOW CALCULATOR
          ============================================================ */}
      <div style={styles.formSection}>
        <form
          className="gs-form-card"
          style={styles.formCard}
          onSubmit={handleSubmit}
        >
          <h2 style={styles.formHeading}>
            Get Your Free{" "}
            <span style={styles.formHeadingAccent}>Solar Site Visit</span>
          </h2>
          <p style={styles.formSubheading}>It takes less than 60 seconds!</p>

          <div className="gs-form-grid3" style={styles.formGrid3}>
            <div>
              <label style={styles.fieldLabel}>Full Name*</label>
              <div className="gs-input-wrap" style={styles.inputWrap}>
                <span style={styles.inputIcon}><FaUser /></span>
                <input
                  className="gs-input"
                  style={styles.input}
                  type="text"
                  placeholder="Enter your full name"
                  value={form.name}
                  onChange={handleChange("name")}
                  required
                />
              </div>
            </div>

            <div>
              <label style={styles.fieldLabel}>PIN Code*</label>
              <div className="gs-input-wrap" style={styles.inputWrap}>
                <span style={styles.inputIcon}><FaMapMarkerAlt /></span>
                <input
                  className="gs-input"
                  style={styles.input}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter 6-digit PIN code"
                  value={form.pin}
                  onChange={handleChange("pin")}
                  required
                />
              </div>
            </div>

            <div>
              <label style={styles.fieldLabel}>WhatsApp Number*</label>
              <div className="gs-input-wrap" style={styles.inputWrap}>
                <span style={styles.inputIcon}><FaWhatsapp /></span>
                <span style={styles.prefix}>+91</span>
                <input
                  className="gs-input"
                  style={styles.input}
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="Enter WhatsApp number"
                  value={form.whatsapp}
                  onChange={handleChange("whatsapp")}
                  required
                />
              </div>
            </div>

            <div>
              <label style={styles.fieldLabel}>Sales Representative*</label>

              <div className="gs-input-wrap" style={styles.inputWrap}>
                <span style={styles.inputIcon}>
                  <FaUser />
                </span>

                <select
                  className="gs-input"
                  style={styles.input}
                  value={form.salesRepresentative}
                  onChange={handleChange("salesRepresentative")}
                >
                  <option value="">Select representative</option>
                  <option value="Priyam">Priyam</option>
                  <option value="Ikram">Ikram</option>
                </select>
              </div>
            </div>

            {/* <div>
              <label style={styles.fieldLabel}>Email*</label>
              <div className="gs-input-wrap" style={styles.inputWrap}>
                <span style={styles.inputIcon}><FaMailBulk /></span>
                <span style={styles.prefix}></span>
                <input
                  className="gs-input"
                  style={styles.input}
                  type="text"
                  inputMode="text"
                  placeholder="Enter Email"
                  value={form.email}
                  onChange={handleChange("email")}
                  required
                />
              </div>
            </div> */}
          </div>

          <label style={styles.sectionLabel}>
            What are you looking for?*
          </label>
          <div className="gs-option-grid4" style={styles.optionGrid4}>
            {lookingForOptions.map((opt) => (
              <div
                key={opt.key}
                style={styles.optionCard(lookingFor === opt.key)}
                onClick={() => setLookingFor(opt.key)}
                role="button"
                tabIndex={0}
              >
                <span style={styles.optionIcon}><opt.icon /></span>
                <span style={styles.optionLabel}>{opt.label}</span>
              </div>
            ))}
          </div>

          <label style={styles.sectionLabel}>
            Your average monthly electricity bill*
          </label>
          <div className="gs-radio-grid" style={styles.radioGrid}>
            {billOptions.map((opt) => (
              <div
                key={opt}
                style={styles.radioRow(bill === opt)}
                onClick={() => setBill(opt)}
                role="button"
                tabIndex={0}
              >
                <div style={styles.radioDot(bill === opt)}>
                  {bill === opt && <div style={styles.radioDotInner} />}
                </div>
                {opt}
              </div>
            ))}
          </div>

          <button
            type="submit"
            style={styles.submitBtn(isSubmitting)}
            disabled={isSubmitting}
          >
            {isSubmitting ? "SUBMITTING..." : "GET SITE VISIT"}{" "}
            <FaArrowRight />
          </button>

          {status === "error" && (
            <div style={styles.statusBanner("error")}>{errorMessage}</div>
          )}
          {status === "success" && (
            <div style={styles.statusBanner("success")}>
              Thanks! Our team will contact you shortly about your free site visit.
            </div>
          )}

          <div style={styles.submitNote}>
            <FaLock /> Your information is safe and will never be shared.
          </div>
        </form>
      </div>

      {/* Why choose */}
      <div style={styles.whySection}>
        <div style={styles.whyInner}>
          <div style={styles.whyHeader}>
            <h2 style={styles.whyHeading}>
              Why <span style={styles.whyHeadingAccent}>Gautam Solar</span>{" "}
              is the right choice.
            </h2>
            <p style={styles.whySub}>
              Tier-1 manufacturing, 30+ years of Indian solar expertise,
              and end-to-end support from design to installation.
            </p>
          </div>

          <div className="gs-why-grid" style={styles.whyGrid}>
            <WhyCard
              accent
              icon={<FaSun />}
              title="Advanced Solar Technology"
              text="High-efficiency modules engineered specifically for Indian climate, dust, and heat conditions."
            />
            <WhyCard
              icon={<FaRobot />}
              title="Tier 1 Robotic Manufacturing"
              text="Fully automated production lines ensure consistent quality and reliability across every panel."
            />
            <WhyCard
              icon={<FaCogs />}
              title="End-to-End Solar Solutions"
              text="From rooftop survey and system design to installation, net-metering, and lifetime support."
            />
            <WhyCard
              accent
              icon={<FaChartLine />}
              title="High Performance & Savings"
              text="Industry-leading performance ratios translate to lower bills and faster payback periods."
            />
          </div>
        </div>
      </div>

      {/* Banner */}
      <div style={styles.banner}>
        <img
          src="/ribbon-banner.jpeg"
          alt="ribbon banner"
          style={styles.bannerImage}
        />
      </div>
    </div>
  );
}